import { gql, useQuery } from '@apollo/client';
import { cx } from '@orn/shared-web/utils/cx';
import formatPacificDateTime from '@orn/shared-web/utils/datetime/formatPacificDateTime';
import * as React from 'react';

import Header from '../../components/Header';

import ChannelAttentionPanel from './ChannelAttentionPanel';
import ChannelFailuresPanel from './ChannelFailuresPanel';
import { CHANNEL_HEALTH_FIELDS } from './channelHealthFields';
import { CHANNEL_HEALTH_WINDOWS, DEFAULT_CHANNEL_HEALTH_WINDOW_DAYS } from './channelHealthView';
import ChannelMetricsTable from './ChannelMetricsTable';
import SegmentedButtons from './SegmentedButtons';

import type {
  AdminGetChannelHealthQuery,
  AdminGetChannelHealthQueryVariables,
} from './__generated__/ChannelHealthScene.gql';

const ADMIN_GET_CHANNEL_HEALTH = gql`
  query AdminGetChannelHealth($windowDays: Int!) {
    adminGetChannelHealth(windowDays: $windowDays) {
      since
      activeMembers
      unreachableMembers
      channels {
        channel
        states {
          state
          members
        }
        optOutSources {
          source
          members
        }
        membersWithDelivery
        membersWithFailure
        membersWithSpamReport
        receipts {
          sent
          delivered
          failed
          unreported
          rearmed
          sendLatency {
            measured
            p50Ms
            p90Ms
          }
          deliveryLatency {
            measured
            p50Ms
            p90Ms
          }
        }
        failureCodes {
          code
          faultClass
          openings
        }
      }
      attention {
        truncated
        total
        items {
          userId
          familyId
          displayName
          endpoint
          health {
            ...ChannelHealthFields
          }
        }
      }
    }
  }
  ${CHANNEL_HEALTH_FIELDS}
`;

const WINDOW_OPTIONS = CHANNEL_HEALTH_WINDOWS.map(({ days, label }) => ({ value: days, label }));

/**
 * Channel health tool: whether texts and email reach every active member. Per
 * channel, the members in each state now, what the outreach receipts said over
 * the picked window, and the failures by provider code; below, the channels an
 * operator should look at, each opening its household in Accounts. Read-only.
 *
 * Switching the window keeps the last report on screen, dimmed, until the next
 * one lands, so the layout does not collapse to a loading line and back.
 */
const ChannelHealthScene = () => {
  const [windowDays, setWindowDays] = React.useState(DEFAULT_CHANNEL_HEALTH_WINDOW_DAYS);
  const { data, previousData, loading, error } = useQuery<
    AdminGetChannelHealthQuery,
    AdminGetChannelHealthQueryVariables
  >(ADMIN_GET_CHANNEL_HEALTH, {
    variables: { windowDays },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const report = (data ?? previousData)?.adminGetChannelHealth ?? null;
  const isStale = data === undefined && report !== null;

  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-5 px-6 py-5">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-sm font-semibold uppercase tracking-widest">Channel health</h1>
          <SegmentedButtons label="Window" options={WINDOW_OPTIONS} value={windowDays} onChange={setWindowDays} />
        </div>

        {error !== undefined && (
          <div className="text-fg-error-primary text-xs">Could not read the report: {error.message}</div>
        )}

        {report === null && loading && <span className="text-fg-secondary text-xs">Loading…</span>}

        {report !== null && (
          <div className={cx('flex flex-col gap-6 transition-opacity', isStale && 'opacity-50')} aria-busy={loading}>
            <p className="text-fg-tertiary text-xs">
              <span className="text-fg-primary tabular-nums">{report.activeMembers}</span> active members ·{' '}
              <span className="text-fg-primary tabular-nums">{report.unreachableMembers}</span> reachable on neither
              channel · window from <span className="font-mono">{formatPacificDateTime(report.since)}</span>
            </p>
            <div className="grid grid-cols-[minmax(0,5fr)_minmax(0,7fr)] items-start gap-8">
              <ChannelMetricsTable channels={report.channels} />
              <ChannelFailuresPanel channels={report.channels} />
            </div>
            <ChannelAttentionPanel attention={report.attention} />
          </div>
        )}
      </main>
    </div>
  );
};

export default ChannelHealthScene;
